/**
 * RecordSaleDialog
 *
 * "Potwierdź sprzedaż" — records the sale on a CONFIRMED campaign, closing
 * its still-open reservations as SOLD. Renders its own `ModalDialog` shell
 * (not `FinalizeConfirmDialog`, which has no body slot) so it can show a
 * breakdown table and a not-currently-accepted opt-in ahead of confirming
 * (sale-breakdown-not-accepted-units / "Sale action in the offer
 * workspace").
 *
 * Fetches the breakdown (`useSalePreview`) before the caller can confirm;
 * the confirm action stays disabled while that fetch is in flight or has
 * failed, so a click is never sent with no known breakdown. Joins the
 * preview's `{mediaUnitId, currentlyAccepted, periods, reservationStatus}`
 * rows against the offer's full unit list (`useOfferUnitsAll`) by
 * `mediaUnitId` so the breakdown table can render the caller's own
 * configured offer-table columns (address/format/etc.) alongside them —
 * both the join and the post-confirm toast logic live in `use-record-sale.ts`
 * to keep this component a thin composition of dialog chrome + body.
 */
import * as React from 'react';
import type { Offer } from '@ooh/contracts';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { useSalePreview } from '../api';
import { useOfferUnitsAll } from '../units-tab/api/queries';
import { useRecordSaleColumns } from './record-sale-columns';
import { useSaleBreakdown, useRecordSaleHandler } from './use-record-sale';
import { SaleBreakdownBody } from './sale-breakdown-body';
import { getSaleDialogSubtitle } from './sale-dialog-subtitle';

const RECORD_SALE_DIALOG_MAX_WIDTH = 'max-w-4xl';

export function RecordSaleDialog({ offer, onClose }: { offer: Offer; onClose: () => void }) {
  const { t } = useTranslation('offers');
  const [includeNotCurrentlyAccepted, setIncludeNotCurrentlyAccepted] = React.useState(false);
  // Dialog only mounts while open, so both queries are always enabled here.
  const salePreview = useSalePreview(String(offer.id));
  const allUnits = useOfferUnitsAll(offer.id, { enabled: true });
  const breakdown = useSaleBreakdown(salePreview, allUnits, includeNotCurrentlyAccepted);
  const { markOfferSold, handleRecordSale } = useRecordSaleHandler(
    offer,
    onClose,
    includeNotCurrentlyAccepted,
    t,
  );
  const columns = useRecordSaleColumns(t, includeNotCurrentlyAccepted);

  const confirmDisabled = markOfferSold.isPending || breakdown.isLoading || breakdown.isError;
  const showBreakdown = !breakdown.isLoading && !breakdown.isError && breakdown.totalCount > 0;

  return (
    <ModalDialog
      testId="record-sale-dialog"
      maxWidth={RECORD_SALE_DIALOG_MAX_WIDTH}
      closeLabel={t('workspace.header.saleDialog.close')}
      onClose={onClose}
      header={
        <div>
          <h2 className="text-base font-semibold">{t('workspace.header.saleDialog.title')}</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {getSaleDialogSubtitle(t, breakdown)}
          </p>
        </div>
      }
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('workspace.header.saleDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="success"
            disabled={confirmDisabled}
            onClick={() => void handleRecordSale()}
          >
            {t('workspace.header.saleDialog.confirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {showBreakdown ? (
          <SaleBreakdownBody
            t={t}
            closingCount={breakdown.closingCount}
            skippedCount={breakdown.skippedCount}
            notCurrentlyAcceptedCount={breakdown.notCurrentlyAcceptedCount}
            includeNotCurrentlyAccepted={includeNotCurrentlyAccepted}
            onIncludeChange={setIncludeNotCurrentlyAccepted}
            columns={columns}
            rows={breakdown.rows}
          />
        ) : null}

        <p className="text-sm text-muted-foreground">{t('workspace.header.saleDialog.helpText')}</p>
      </div>
    </ModalDialog>
  );
}
